import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BELEG_SICHERHEITSLIMIT_MS, type BedienerKurz } from '../api';
import { aktuelleRechte, ladeAnmeldung, type BedienerRechte } from '../bediener';
import { aktuelleKennung } from '../belegnummern';
import { istGedruckt, merkeGedruckt, type GeraeteRueckmeldung } from '../bondruck';
import type { BonDruck } from '../bonweg';
import type { GeraeteSpeicher } from '../geraetespeicher';
import { BedienerAnmeldung } from '../komponenten/BedienerAnmeldung';
import { Belegansicht } from '../komponenten/Belegansicht';
import {
  Bildschirmtastatur,
  istTastaturEsc,
  TastaturKnopf,
  useBildschirmtastatur,
} from '../komponenten/Bildschirmtastatur';
import { useScanSchutz } from '../scan-schutz';
import { formatiereEuro } from '../warenkorb';
import {
  BelegNichtGefunden,
  OriginalMehrdeutig,
  RetoureAbgelehnt,
  RetoureUnklar,
  type FreigabeErgebnis,
  type GrundCode,
  type RetoureAbschluss,
  type RetoureAnfrage,
  type RetoureApi,
  type RetoureErgebnis,
  type RetoureOriginal,
  type RetourePosition,
  type RetoureVorschau,
  type Zahlart,
  type Zustand,
} from './api';
import { deuteEingabe } from './belegsuche';
import { HAUPTKASSE_TEXT, OFFLINE_TEXT, retoureGeraetPasst } from './schalter';

export { OFFLINE_TEXT, retoureEinstiegSperre } from './schalter';
import { STATUS_PRUEFUNG_MS, systemSperre, type SystemStand } from './entwurf';
import { leseMenge, mengeFehler, mengeText, schrittMenge, tausendstel } from './menge';
import {
  aktualisiereRetoureVorgang,
  beanspruche,
  entferneRetoureVorgang,
  entferneRetoureEntwurf,
  fensterBeanspruchen,
  ladeRetoureEntwurf,
  sichereRetoureEntwurf,
  ladeRetoureVorgang,
  neueVorgangId,
  sichereRetoureVorgang,
  uebernimmRetoureVorgang,
  type RetoureVorgangDatensatz,
} from './vorgang';
import './retoure.css';

/**
 * Retoure Stufe 2 – Kassenoberfläche (Figma „Retoure v2 – ENTWURF“, Rahmen RT2-1 … RT2-13;
 * Aufbau aus RT-FINAL „B + Betragsband aus C“, Section 277:10482).
 *
 * Einstieg über „Mehr“ → „Retoure“ (F8) im Verkauf. Ein eigener Vorgang: Der laufende Bon bleibt
 * unberührt (der Verkauf sichert ihn beim Wechsel wie bei „Belege“).
 *
 * Sicherheitsregeln (Sicherheitskonzept Abschnitt 4, Fachkonzept 8.2/12):
 * - **Beträge nur vom Server** (Vorschau bzw. Abschluss); die Kasse rechnet keine Beträge.
 * - **Genau einmal bestätigen:** Die Taste sperrt synchron; die Vorgangs-ID wird vor dem Senden im
 *   Gerätespeicher abgelegt und bleibt bei jeder Wiederholung (auch nach Neuladen) gleich.
 * - **Unklarer Ausgang** (Netz, Zeitlimit, 5xx, Konflikt): Klärungsanzeige „Retoure wird geprüft –
 *   nicht erneut auszahlen“; geklärt wird nur über die Abfrage dieser Vorgangs-ID. Es wird nie
 *   selbsttätig ein zweites Mal gesendet.
 * - **Druck und Lade erst nach dem signierten Retourenbeleg**, je Vorgang höchstens einmal.
 * - **Nur online**, nicht im Training, nicht bei gesperrter Kasse bzw. Sicherheitseinrichtung.
 */

type Schritt = 'beleg' | 'artikel' | 'grund' | 'erstattung';

const SCHRITTE: readonly { id: Schritt; text: string }[] = [
  { id: 'beleg', text: 'Beleg finden' },
  { id: 'artikel', text: 'Artikel' },
  { id: 'grund', text: 'Grund und Zustand' },
  { id: 'erstattung', text: 'Erstattung' },
];

/**
 * Gründe wie Figma RT2-4 und API-Vertrag 3.3: ein Grund für den ganzen Vorgang. „Mangel /
 * Gewährleistung“ fällt unter „Defekt“ (O4); „Umtausch“ ist nur ein Grund – der neue Artikel wird
 * danach normal verkauft (A7).
 */
export const GRUENDE: readonly { id: GrundCode; text: string }[] = [
  { id: 'defekt', text: 'Defekt' },
  { id: 'fehlkauf', text: 'Fehlkauf' },
  { id: 'umtausch', text: 'Umtausch' },
  { id: 'sonstiges', text: 'Sonstiges' },
];

/** O5: keine Fristsperre, ab 14 Tagen nur ein Hinweis. */
export const KULANZ_TAGE = 14;
export function kulanzHinweis(zeitpunkt: string, jetzt: number): string | null {
  const t = Date.parse(zeitpunkt);
  return !Number.isNaN(t) && jetzt - t >= KULANZ_TAGE * 24 * 60 * 60 * 1000
    ? `älter als ${String(KULANZ_TAGE)} Tage – Kulanz`
    : null;
}

export const ZUSTAENDE: readonly { id: Zustand; text: string }[] = [
  { id: 'verkaufsfaehig', text: 'Verkaufsfähig' },
  { id: 'beschaedigt', text: 'Beschädigt' },
];

export const KLAERUNG_TEXT = 'Retoure wird geprüft – nicht erneut auszahlen';

/** Solange kann ein gesendeter Abschluss noch unterwegs sein (Zeitlimit Beleg + Reserve). */
export const SENDE_FRIST_MS = BELEG_SICHERHEITSLIMIT_MS + 30_000;
/** Nach einem unklaren Ausgang: Nachlauf, bevor „Erneut senden“/„Abbrechen“ möglich ist. */
export const NACHLAUF_MS = 30_000;
/** Abgleich offener Vorgänge zwischen Fenstern. */
const ABGLEICH_MS = 2_000;
/** Ablehnungen, nach denen eine neue Freigabe nötig ist. */
const FREIGABE_CODES = new Set([
  'freigabe_noetig',
  'freigabe_ungueltig',
  'freigabe_ohne_bedienerverwaltung',
]);
export const GRUND_TEXT_MAX = 200;

/** Sperrtext für Karte, Gutschein und Mischzahlung, wenn der Server keinen eigenen nennt. */
export const KARTE_GESPERRT_TEXT =
  'Kartenerstattung nicht freigegeben – bitte Betreiber kontaktieren';

const ART_TEXT: Record<Zahlart, string> = {
  bar: 'Bar',
  karte: 'Karte',
  gutschein: 'Gutschein',
  gemischt: 'Mischzahlung',
};

function euro(cent: number): string {
  return Number.isNaN(cent) ? '–' : `${formatiereEuro(cent)} €`;
}

function datum(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('de-AT', {
        timeZone: 'Europe/Vienna',
        dateStyle: 'short',
        timeStyle: 'short',
      });
}

function useOnline(vorgabe: boolean | undefined): boolean {
  const [online, setOnline] = useState(
    () => vorgabe ?? (typeof navigator === 'undefined' ? true : navigator.onLine),
  );
  useEffect(() => {
    if (vorgabe !== undefined) {
      setOnline(vorgabe);
      return;
    }
    const an = (): void => {
      setOnline(true);
    };
    const aus = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', an);
    window.addEventListener('offline', aus);
    return () => {
      window.removeEventListener('online', an);
      window.removeEventListener('offline', aus);
    };
  }, [vorgabe]);
  return online;
}

/** Auswahl je Positionsindex: Menge (Zahl), getippter Text (kg) und Zustand. */
interface Wahl {
  menge: number;
  eingabe: string | null;
  zustand: Zustand | null;
}

/** Auswahl als Vertragsanfrage (Positionen sortiert, nur Mengen > 0). */
function anfrageAus(
  original: RetoureOriginal,
  auswahl: ReadonlyMap<number, Wahl>,
  grund: GrundCode | null,
  grundText: string,
): RetoureAnfrage | null {
  const positionen = [...auswahl]
    .filter(([, w]) => tausendstel(w.menge) > 0)
    .sort(([a], [b]) => a - b);
  if (grund === null || positionen.some(([, w]) => w.zustand === null)) {
    return null;
  }
  const text = grundText.trim().slice(0, GRUND_TEXT_MAX);
  return {
    original: { kassenId: original.kassenId, belegnummer: original.belegnummer },
    grund,
    grundText: text === '' ? null : text,
    positionen: positionen.map(([index, w]) => ({
      index,
      menge: w.menge,
      zustand: w.zustand ?? 'beschaedigt',
    })),
    erstattungsart: 'bar',
  };
}

/** Gleiche Nutzdaten = gleicher Schlüssel (wie der Hash des Servers, Vertrag 3.1). */
function vorschauSchluessel(a: RetoureAnfrage): string {
  return JSON.stringify([
    a.original.kassenId,
    a.original.belegnummer,
    a.positionen.map((p) => [p.index, tausendstel(p.menge), p.zustand]),
    a.grund,
    a.grundText,
    a.erstattungsart,
  ]);
}

export interface RetoureProps {
  api: RetoureApi;
  kassenId: string;
  geraeteSpeicher: GeraeteSpeicher;
  /** Zurück zum Verkauf (der Bon dort ist unverändert). */
  onFertig: () => void;
  /** Rechte des angemeldeten Bedieners; Vorgabe aus der Sitzung. */
  rechte?: Pick<BedienerRechte, 'retoure'>;
  /** Verbindungszustand; Vorgabe `navigator.onLine`. */
  online?: boolean;
  /** Sperre aus dem Zustand der Kasse (`kassenSperreFuerRetoure`), null = keine. */
  kassenSperre?: string | null;
  /** Bondruck (vorhandener Druckweg); ohne: Bildschirmbeleg mit Hinweis. */
  bridgeDruck?: BonDruck;
  /** Kassenlade für den Retourenbeleg (nativ mit Schlüssel je Beleg bzw. Server-Weg). */
  ladeOeffnen?: (belegnummer: string) => Promise<GeraeteRueckmeldung>;
  /** Bediener für die Freigabe (PIN); ohne: keine Freigabe möglich. */
  bedienerListe?: () => Promise<BedienerKurz[]>;
  /** Gerätekennung für den Abschluss; Vorgabe `aktuelleKennung` (Gerätespeicher). */
  geraetekennung?: () => Promise<string>;
  /** O10: T3/Desktop (ab 1280 px); Vorgabe aus der Fensterbreite. */
  geraetPasst?: boolean;
}

/** Fenster beanspruchen (Web Lock), dann die Retoure anzeigen. */
export function Retoure(props: RetoureProps) {
  const [fenster, setFenster] = useState<string | null>(null);
  useEffect(() => {
    let aktiv = true;
    void fensterBeanspruchen().then((f) => {
      if (aktiv) {
        setFenster(f);
      }
    });
    return () => {
      aktiv = false;
    };
  }, []);
  if (fenster === null) {
    return (
      <div className="retoure">
        <p className="hinweis">Retoure wird geladen …</p>
      </div>
    );
  }
  return <RetoureInhalt {...props} fenster={fenster} />;
}

function RetoureInhalt(props: RetoureProps & { fenster: string }) {
  const { api, kassenId, geraeteSpeicher, fenster } = props;
  const online = useOnline(props.online);
  const rechte = props.rechte ?? aktuelleRechte(sessionStorage, kassenId);
  const [hinweis, setHinweis] = useState<string | null>(null);
  // Ohne Verkauf ist kein Scan-Listener aktiv: Enter eines Scans löst nie eine Taste aus
  useScanSchutz(() => {
    setHinweis('Scan nicht übernommen – zum Scannen zuerst das Belegfeld antippen.');
  });

  /* ── Server: Fähigkeit und Kassenstatus ──────────────────────────────────────────── */
  const [faehig, setFaehig] = useState<{ verfuegbar: boolean; grund: string | null } | null>(null);
  useEffect(() => {
    if (!online) {
      return;
    }
    let aktiv = true;
    api
      .faehigkeit(kassenId)
      .then((f) => {
        if (aktiv) {
          setFaehig(f);
        }
      })
      .catch(() => {
        if (aktiv) {
          setFaehig({ verfuegbar: false, grund: 'Retoure am Server nicht erreichbar.' });
        }
      });
    return () => {
      aktiv = false;
    };
  }, [api, kassenId, online]);

  const [system, setSystem] = useState<SystemStand>({ art: 'pruefung' });
  const abruf = useRef(0);
  const statusPruefen = useCallback(async (): Promise<SystemStand | null> => {
    const kennung = ++abruf.current;
    let neu: SystemStand;
    try {
      neu = { art: 'ok', status: await api.status(kassenId), geprueftAm: Date.now() };
    } catch {
      neu = { art: 'fehler' };
    }
    if (kennung !== abruf.current) {
      return null; // überholt
    }
    setSystem(neu);
    return neu;
  }, [api, kassenId]);
  useEffect(() => {
    if (!online) {
      return;
    }
    void statusPruefen();
    const t = setInterval(() => {
      void statusPruefen();
    }, STATUS_PRUEFUNG_MS);
    return () => {
      clearInterval(t);
      abruf.current++;
    };
  }, [online, statusPruefen]);

  const kassenSperre = props.kassenSperre ?? null;
  const geraetPasst = props.geraetPasst ?? retoureGeraetPasst();
  const sperre: string | null = !geraetPasst
    ? HAUPTKASSE_TEXT
    : !online
      ? OFFLINE_TEXT
      : faehig === null
        ? 'Retoure wird geprüft …'
        : !faehig.verfuegbar
          ? (faehig.grund ?? 'Retoure ist für diese Kasse nicht freigeschaltet.')
          : rechte.retoure === false
            ? 'Kein Recht „Retoure“ für den angemeldeten Bediener.'
            : systemSperre(system, kassenSperre, Date.now());
  const gesperrt = sperre !== null;

  /* ── Vorgang auf dem Gerät (Klärung bzw. Ergebnis nach Neuladen) ──────────────────── */
  const [vorgang, setVorgang] = useState<RetoureVorgangDatensatz | null | 'laedt'>('laedt');
  // Abgeschlossener Vorgang aus dem Gerätespeicher (Neuladen): Auszahlungsstand beim Server prüfen
  const [ausSpeicher, setAusSpeicher] = useState(false);
  useEffect(() => {
    let aktiv = true;
    ladeRetoureVorgang(geraeteSpeicher, kassenId)
      .then((d) => {
        if (aktiv) {
          setAusSpeicher(d?.phase === 'abgeschlossen');
          setVorgang(d);
        }
      })
      .catch(() => {
        if (aktiv) {
          setVorgang(null);
          setHinweis('Gerätespeicher nicht lesbar – Retoure gesperrt.');
        }
      });
    return () => {
      aktiv = false;
    };
  }, [geraeteSpeicher, kassenId]);
  const speicherFehler = hinweis === 'Gerätespeicher nicht lesbar – Retoure gesperrt.';

  /* ── Ablauf ──────────────────────────────────────────────────────────────────────── */
  const [schritt, setSchritt] = useState<Schritt>('beleg');
  /** Zwei gleichrangige Wege (Nutzer 04.11.2026): Bon-QR-Scan und Belegnummer. */
  const [qrEingabe, setQrEingabe] = useState('');
  const [eingabe, setEingabe] = useState('');
  /** Sofort abgewiesene Mengen über „noch möglich“ je Zeile (Nutzer 2; oben gemeldet, R3 M7). */
  const [abgewiesen, setAbgewiesen] = useState<ReadonlyMap<number, string>>(new Map());
  const abweisung = useCallback((index: number, text: string | null): void => {
    setAbgewiesen((a) => {
      if (text === null ? !a.has(index) : a.get(index) === text) {
        return a;
      }
      const neu = new Map(a);
      if (text === null) {
        neu.delete(index);
      } else {
        neu.set(index, text);
      }
      return neu;
    });
  }, []);
  const [suchMeldung, setSuchMeldung] = useState<{ feld: 'qr' | 'nummer'; text: string } | null>(
    null,
  );
  const [sucht, setSucht] = useState(false);
  const [original, setOriginal] = useState<RetoureOriginal | null>(null);
  const [auswahl, setAuswahl] = useState<ReadonlyMap<number, Wahl>>(new Map());
  const [grund, setGrund] = useState<GrundCode | null>(null);
  const [grundText, setGrundText] = useState('');
  const [art, setArt] = useState<Zahlart | null>(null);
  /** Freigabe am Server erteilt – gilt nur für genau diese Nutzdaten (Schlüssel). */
  const [freigabe, setFreigabe] = useState<(FreigabeErgebnis & { schluessel: string }) | null>(
    null,
  );
  const [freigabeDialog, setFreigabeDialog] = useState<BedienerKurz[] | null>(null);
  const [freigabeLaeuft, setFreigabeLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Belegnummer an mehreren Kassen: Auswahl der Kasse (409 `original_mehrdeutig`). */
  const [mehrdeutig, setMehrdeutig] = useState<{ belegnummer: string; kassen: string[] } | null>(
    null,
  );
  /**
   * Vorgangs-ID: einmal je Retourenvorgang erzeugt (beim Übernehmen des Originals, vor der
   * ersten Vorschau) und für Vorschau, Freigabe, Abschluss und Auszahlung unverändert genutzt.
   */
  const [vorgangId, setVorgangId] = useState<string | null>(null);

  const bt = useBildschirmtastatur({ standardZiel: 'beleg' });
  const suchfeld = useRef<HTMLInputElement | null>(null);
  const nummerFeld = useRef<HTMLInputElement | null>(null);
  const ueberschrift = useRef<HTMLHeadingElement>(null);

  // Belegfeld fokussiert halten (Bon-QR per Handscanner landet dort)
  useEffect(() => {
    if (schritt === 'beleg' && original === null && !gesperrt) {
      suchfeld.current?.focus();
    }
  }, [schritt, original, gesperrt]);

  const positionen = original?.positionen ?? [];
  const posVon = (index: number): RetourePosition | undefined =>
    positionen.find((p) => p.index === index);

  /** Mengenfehler je Zeile (lokal, nur Mengen – zu hoch, ungültige Eingabe). */
  const mengenFehler = new Map<number, string>();
  for (const [index, w] of auswahl) {
    const p = posVon(index);
    if (p === undefined) {
      continue;
    }
    if (w.eingabe !== null) {
      const r = leseMenge(w.eingabe, p.einheit);
      if (!r.ok) {
        mengenFehler.set(index, r.fehler);
        continue;
      }
    }
    const f = mengeFehler(w.menge, p.moeglich, p.einheit);
    if (f !== null) {
      mengenFehler.set(index, f);
    }
  }
  const gewaehlt = [...auswahl].filter(([, w]) => tausendstel(w.menge) > 0);

  const grundFehlt =
    grund === null
      ? 'Rückgabegrund wählen'
      : grund === 'sonstiges' && grundText.trim() === ''
        ? 'Bei „Sonstiges“ ist der Text Pflicht'
        : null;
  const zustandFehlt = gewaehlt
    .filter(([, w]) => w.zustand === null)
    .map(([i]) => `${posVon(i)?.bezeichnung ?? 'Position'}: Zustand wählen`);

  /*
   * ── Server-Vorschau (einzige Quelle aller Beträge) ──
   * Die Vorschau prüft wie der Abschluss auch Grund und Zustand (Vertrag 4.2); einen Betrag gibt
   * es daher erst nach Schritt 3. Abweichung von Figma RT2-3 (Betragsband schon bei den Artikeln).
   */
  const anfrage =
    original === null || gewaehlt.length === 0 || mengenFehler.size > 0 || grundFehlt !== null
      ? null
      : anfrageAus(original, auswahl, grund, grundText);
  const [vorschau, setVorschau] = useState<{ schluessel: string; v: RetoureVorschau } | null>(null);
  const [vorschauLaedt, setVorschauLaedt] = useState(false);
  const [vorschauFehler, setVorschauFehler] = useState<string | null>(null);
  const schluessel = anfrage === null ? null : vorschauSchluessel(anfrage);
  const anfrageRef = useRef(anfrage);
  anfrageRef.current = anfrage;
  const vorschauNr = useRef(0);
  // E-S1: Nach einer Ablehnung wegen der kumulierten Freigabegrenze (eine andere Kasse hat
  // inzwischen zum selben Original retourniert) die Vorschau mit frischen Summen neu laden
  const [vorschauRunde, setVorschauRunde] = useState(0);
  useEffect(() => {
    vorschauNr.current++;
    const nr = vorschauNr.current;
    const a = anfrageRef.current;
    if (a === null || schluessel === null || vorgangId === null || !online) {
      setVorschau(null);
      setVorschauLaedt(false);
      setVorschauFehler(null);
      return;
    }
    setVorschauLaedt(true);
    setVorschauFehler(null);
    const t = setTimeout(() => {
      api
        .vorschau(kassenId, vorgangId, a)
        .then((v) => {
          if (nr === vorschauNr.current) {
            setVorschau({ schluessel, v });
          }
        })
        .catch((e: unknown) => {
          if (nr === vorschauNr.current) {
            setVorschau(null);
            setVorschauFehler(
              e instanceof RetoureAbgelehnt
                ? e.message
                : `Vorschau nicht möglich: ${e instanceof Error ? e.message : String(e)}`,
            );
          }
        })
        .finally(() => {
          if (nr === vorschauNr.current) {
            setVorschauLaedt(false);
          }
        });
    }, 250);
    return () => {
      clearTimeout(t);
    };
    // `anfrage` ist durch `schluessel` vollständig bestimmt
  }, [api, kassenId, vorgangId, schluessel, online, vorschauRunde]);
  const aktuell = vorschau !== null && vorschau.schluessel === schluessel ? vorschau.v : null;

  // Erstattungsart: in Stufe 2 nur bar, und nur wenn der Server sie für das Original zulässt
  const barErlaubt = original?.erstattungsart === 'bar';
  useEffect(() => {
    setArt(barErlaubt ? 'bar' : null);
  }, [barErlaubt]);
  // Eine Freigabe gilt nur für dieselben Nutzdaten
  // … und nur bis zum Ablauf laut Server (`gueltigBis`, 5 Minuten)
  const freigabeGilt =
    freigabe !== null &&
    freigabe.schluessel === schluessel &&
    (freigabe.gueltigBis === null || Date.parse(freigabe.gueltigBis) > Date.now());

  /* ── Beleg finden ────────────────────────────────────────────────────────────────── */
  async function suche(quelle: 'qr' | 'nummer', kasseGewaehlt?: string): Promise<void> {
    if (gesperrt || sucht) {
      return;
    }
    const roh = quelle === 'qr' ? qrEingabe : eingabe;
    const e = deuteEingabe(roh);
    setSuchMeldung(null);
    const melde = (text: string): void => {
      setSuchMeldung({ feld: quelle, text });
    };
    let ziel: { qr: string } | { belegnummer: string; kassenId: string | null };
    if (e.art === 'leer') {
      melde(quelle === 'qr' ? 'Bon-QR scannen.' : 'Belegnummer eingeben.');
      return;
    }
    if (e.art === 'qr-ungueltig') {
      melde('Der gescannte Code ist kein gültiger Beleg-QR-Code. Bon erneut scannen.');
      return;
    }
    if (e.art === 'qr') {
      // vollständiger Datenblock ab „_R1-“ (Vertrag 4.1 `?qr=`); Kasse kommt aus dem Code
      const t = roh.trim();
      ziel = { qr: t.slice(t.indexOf('_R1-')) };
    } else if (e.art === 'nummer') {
      ziel = { belegnummer: e.belegnummer, kassenId: kasseGewaehlt ?? null };
    } else {
      melde(
        quelle === 'qr'
          ? 'Kein Beleg-QR-Code erkannt. Den QR-Code auf dem Bon scannen.'
          : 'Bitte die Belegnummer (z. B. B-20261104-T3-0042) eingeben.',
      );
      return;
    }
    setSucht(true);
    try {
      const o = await api.original(kassenId, ziel);
      // neuer Vorgang: neue ID, vor der ersten Vorschau im Gerätespeicher (Codex R2 M4)
      const id = neueVorgangId();
      try {
        await sichereRetoureEntwurf<Wahl>(geraeteSpeicher, kassenId, {
          vorgangId: id,
          original: { kassenId: o.kassenId, belegnummer: o.belegnummer },
          auswahl: [],
          grund: null,
          grundText: '',
          schritt: 'beleg',
        });
      } catch {
        melde('Gerätespeicher nicht verfügbar – Retoure nicht möglich.');
        return;
      }
      setOriginal(o);
      setMehrdeutig(null);
      setAuswahl(new Map());
      setAbgewiesen(new Map());
      setGrund(null);
      setGrundText('');
      setFreigabe(null);
      setVorgangId(id);
    } catch (f: unknown) {
      setOriginal(null);
      if (f instanceof OriginalMehrdeutig && 'belegnummer' in ziel) {
        setMehrdeutig({ belegnummer: ziel.belegnummer, kassen: f.kassen });
        melde(f.message);
      } else {
        setMehrdeutig(null);
        melde(
          f instanceof BelegNichtGefunden || f instanceof RetoureAbgelehnt
            ? f.message
            : `Suche nicht möglich – ${OFFLINE_TEXT}. Bitte erneut versuchen.`,
        );
      }
      (quelle === 'qr' ? suchfeld : nummerFeld).current?.focus();
    } finally {
      setSucht(false);
    }
  }

  // Entwurf fortschreiben (Auswahl, Grund, Schritt) – nur für den laufenden Vorgang
  useEffect(() => {
    if (original === null || vorgangId === null) {
      return;
    }
    void sichereRetoureEntwurf<Wahl>(geraeteSpeicher, kassenId, {
      vorgangId,
      original: { kassenId: original.kassenId, belegnummer: original.belegnummer },
      auswahl: [...auswahl],
      grund,
      grundText,
      schritt,
    }).catch(() => undefined);
  }, [geraeteSpeicher, kassenId, original, vorgangId, auswahl, grund, grundText, schritt]);

  // Nach Neuladen: Entwurf mit derselben Vorgangs-ID fortsetzen (Original neu vom Server)
  const entwurfGeprueft = useRef(false);
  useEffect(() => {
    if (entwurfGeprueft.current || vorgang !== null || !online) {
      return;
    }
    entwurfGeprueft.current = true;
    void (async () => {
      const ent = await ladeRetoureEntwurf<Wahl>(geraeteSpeicher, kassenId).catch(() => null);
      if (ent === null) {
        return;
      }
      try {
        const o = await api.original(kassenId, {
          belegnummer: ent.original.belegnummer,
          kassenId: ent.original.kassenId,
        });
        const bekannt = new Set(o.positionen.map((p) => p.index));
        setOriginal(o);
        setVorgangId(ent.vorgangId);
        setAuswahl(new Map(ent.auswahl.filter(([i]) => bekannt.has(i))));
        setGrund(GRUENDE.some((g) => g.id === ent.grund) ? (ent.grund as GrundCode) : null);
        setGrundText(ent.grundText);
        setSchritt(SCHRITTE.some((x) => x.id === ent.schritt) ? (ent.schritt as Schritt) : 'beleg');
        setHinweis('Begonnene Retoure fortgesetzt (gleiche Vorgangs-ID).');
      } catch {
        await entferneRetoureEntwurf(geraeteSpeicher, kassenId).catch(() => undefined);
      }
    })();
  }, [api, geraeteSpeicher, kassenId, vorgang, online]);

  function neueRetoure(): void {
    void entferneRetoureEntwurf(geraeteSpeicher, kassenId).catch(() => undefined);
    setOriginal(null);
    setAuswahl(new Map());
    setAbgewiesen(new Map());
    setGrund(null);
    setGrundText('');
    setFreigabe(null);
    setMehrdeutig(null);
    setVorgangId(null);
    setVorschau(null);
    setEingabe('');
    setQrEingabe('');
    setSuchMeldung(null);
    setFehler(null);
    setSchritt('beleg');
  }

  function setzeWahl(index: number, teil: Partial<Wahl>): void {
    setAuswahl((a) => {
      const neu = new Map(a);
      const w = {
        ...(neu.get(index) ?? { menge: 0, eingabe: null, zustand: zustandVorgabe(index) }),
        ...teil,
      };
      if (tausendstel(w.menge) <= 0 && w.eingabe === null) {
        neu.delete(index);
      } else {
        neu.set(index, w);
      }
      return neu;
    });
  }

  /**
   * O11 / Nutzer 04.11.2026 (4): Gewichtsartikel standardmäßig „beschädigt (nicht
   * verkaufsfähig)“, umstellbar; Stück ohne Vorbelegung – ausdrückliche Pflichtwahl, auch bei
   * „Defekt“ (Codex R3 M5).
   */
  function zustandVorgabe(index: number): Zustand | null {
    return posVon(index)?.einheit === 'kg' ? 'beschaedigt' : null;
  }

  function waehleGrund(chip: GrundCode): void {
    setGrund(chip);
  }

  function allesZurueck(): void {
    setAuswahl((a) => {
      const neu = new Map(a);
      for (const p of positionen) {
        if (p.sperrgrund === null && tausendstel(p.moeglich) > 0) {
          neu.set(p.index, {
            menge: p.moeglich,
            eingabe: null,
            zustand: neu.get(p.index)?.zustand ?? zustandVorgabe(p.index),
          });
        }
      }
      return neu;
    });
  }

  const weiterMoeglich: Record<Schritt, boolean> = {
    beleg: original !== null && original.rueckgabefaehig && !original.vollstaendigZurueck,
    artikel: gewaehlt.length > 0 && mengenFehler.size === 0,
    grund: grundFehlt === null && zustandFehlt.length === 0 && aktuell !== null,
    erstattung: false,
  };
  const reihenfolge = SCHRITTE.map((s) => s.id);
  const stelle = reihenfolge.indexOf(schritt);

  function weiter(): void {
    const naechster = reihenfolge[stelle + 1];
    if (naechster !== undefined && weiterMoeglich[schritt] && !gesperrt) {
      setSchritt(naechster);
      setTimeout(() => ueberschrift.current?.focus(), 0);
    }
  }
  function zurueck(): void {
    const voriger = reihenfolge[stelle - 1];
    if (voriger !== undefined) {
      setSchritt(voriger);
    } else if (original !== null) {
      neueRetoure();
    } else {
      props.onFertig();
    }
  }

  /* ── Freigabe (PIN einer berechtigten Person) ────────────────────────────────────── */
  async function freigabeHolen(): Promise<void> {
    if (props.bedienerListe === undefined) {
      setFehler('Freigabe nicht möglich: Bedienerverwaltung nicht verfügbar.');
      return;
    }
    try {
      const liste = await props.bedienerListe();
      // RU-5c: Freigabe nur mit gesetzter PIN; nie der angemeldete Bediener selbst (Selbstfreigabe
      // gesperrt, der Server prüft das zusätzlich); wer berechtigt ist, entscheidet der Server
      const selbst = ladeAnmeldung(sessionStorage, kassenId)?.id ?? null;
      setFreigabeDialog(liste.filter((b) => b.pinNoetig && b.id !== selbst));
    } catch (e) {
      setFehler(`Bediener nicht abrufbar: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  /** PIN an den Server (Vertrag 4.3); die PIN bleibt nie im Zustand oder Gerätespeicher. */
  async function freigabePruefen(bedienerId: number, pin: string): Promise<void> {
    const a = anfrage;
    const k = schluessel;
    if (a === null || k === null || vorgangId === null || freigabeLaeuft) {
      return;
    }
    setFreigabeLaeuft(true);
    setFehler(null);
    try {
      const f = await api.freigabe(kassenId, vorgangId, a, { bedienerId, pin });
      setFreigabe({ ...f, schluessel: k });
    } catch (e) {
      if (e instanceof RetoureAbgelehnt && e.code === 'freigabe_unnoetig') {
        // Server: unter der Grenze – keine Freigabe nötig; die Vorschau gilt
        setFreigabe({ name: 'nicht nötig', gueltigBis: null, schluessel: k });
        return;
      }
      setFreigabe(null);
      setFehler(
        e instanceof RetoureAbgelehnt
          ? `Freigabe abgelehnt: ${e.message}`
          : `Freigabe nicht möglich: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setFreigabeLaeuft(false);
    }
  }

  /* ── Bestätigen: genau einmal ────────────────────────────────────────────────────── */
  const sendetRef = useRef(false);
  const [sendet, setSendet] = useState(false);
  const [klaerung, setKlaerung] = useState<string | null>(null);

  /** Datensatz neu lesen (z. B. nach Verlust des Besitzes an ein anderes Fenster). */
  const neuLesen = useCallback(async (): Promise<void> => {
    setVorgang(await ladeRetoureVorgang(geraeteSpeicher, kassenId).catch(() => null));
  }, [geraeteSpeicher, kassenId]);

  const zeigeAbgeschlossen = useCallback(
    async (d: RetoureVorgangDatensatz, ergebnis: RetoureAbschluss): Promise<void> => {
      setKlaerung(null);
      const neu = await aktualisiereRetoureVorgang(
        geraeteSpeicher,
        d.kassenId,
        d.vorgangId,
        { phase: 'abgeschlossen', ergebnis, sendetBis: Date.now() },
        fenster,
      ).catch(() => null);
      if (neu === null) {
        // nicht mehr in diesem Fenster bzw. Speicher nicht schreibbar: nur anzeigen, was gespeichert ist
        await neuLesen();
        return;
      }
      setVorgang(neu);
    },
    [geraeteSpeicher, fenster, neuLesen],
  );

  /** Ergebnis vom Server verarbeiten (Abschluss oder Abfrage). */
  const verarbeite = useCallback(
    async (d: RetoureVorgangDatensatz, e: RetoureErgebnis): Promise<void> => {
      switch (e.art) {
        case 'abgeschlossen':
          await zeigeAbgeschlossen(d, e);
          return;
        case 'abgelehnt':
          // endgültig: nichts gebucht, nichts auszahlen – der Vorgang ist beendet
          await entferneRetoureVorgang(geraeteSpeicher, d.kassenId, d.vorgangId, fenster).catch(
            () => false,
          );
          if (e.code !== null && FREIGABE_CODES.has(e.code)) {
            setFreigabe(null);
            setVorschauRunde((n) => n + 1);
          }
          setVorgang(null);
          setKlaerung(null);
          setFehler(`Retoure nicht abgeschlossen – nichts auszahlen. ${e.text}`);
          sendetRef.current = false;
          setSendet(false);
          return;
        case 'in_pruefung':
          setVorgang(d);
          setKlaerung(e.text);
          return;
        case 'unbekannt':
          setVorgang(d);
          setKlaerung(
            'Der Server kennt diese Retoure nicht – sie ist nicht angekommen. Erneut senden (gleiche Vorgangs-ID) oder abbrechen.',
          );
      }
    },
    [geraeteSpeicher, fenster, zeigeAbgeschlossen],
  );

  /** Nach unklarem Ausgang: Nachlauf setzen (kein Übernehmen/Abbrechen, solange etwas unterwegs sein kann). */
  async function nachlauf(d: RetoureVorgangDatensatz): Promise<RetoureVorgangDatensatz> {
    return (
      (await aktualisiereRetoureVorgang(
        geraeteSpeicher,
        d.kassenId,
        d.vorgangId,
        { sendetBis: Date.now() + NACHLAUF_MS },
        fenster,
      ).catch(() => null)) ?? d
    );
  }

  async function sende(d: RetoureVorgangDatensatz): Promise<void> {
    try {
      const e = await api.abschliessen(kassenId, d.vorgangId, d.anfrage, d.geraetekennung);
      if (e.art === 'abgeschlossen' || e.art === 'abgelehnt') {
        await verarbeite(d, e);
      } else {
        await verarbeite(await nachlauf(d), e);
      }
    } catch (f) {
      setVorgang(await nachlauf(d));
      setKlaerung(
        f instanceof RetoureUnklar
          ? 'Antwort des Servers fehlt (Netz oder Zeitlimit). Stand wird über die Vorgangs-ID geprüft.'
          : `Ausgang unklar: ${f instanceof Error ? f.message : String(f)}`,
      );
    }
  }

  async function bestaetigen(): Promise<void> {
    // synchron gegen Doppeltipp: eine zweite Berührung findet die Sperre schon gesetzt
    if (sendetRef.current) {
      return;
    }
    if (
      original === null ||
      anfrage === null ||
      vorgangId === null ||
      aktuell === null ||
      zustandFehlt.length > 0 ||
      art !== 'bar' ||
      !barErlaubt ||
      gesperrt ||
      (aktuell.freigabeNoetig && !freigabeGilt)
    ) {
      return;
    }
    sendetRef.current = true;
    setSendet(true);
    setFehler(null);
    // Kassenstatus frisch prüfen (Ausfall der Sicherheitseinrichtung mitten im Ablauf)
    const stand = await statusPruefen();
    if (stand === null || systemSperre(stand, kassenSperre, Date.now()) !== null) {
      setFehler('Kassenstatus verhindert die Retoure – nichts gesendet.');
      sendetRef.current = false;
      setSendet(false);
      return;
    }
    let d: RetoureVorgangDatensatz;
    try {
      const kennung = await (
        props.geraetekennung ?? (() => aktuelleKennung(geraeteSpeicher, localStorage))
      )();
      // vor dem Senden sichern (N1); liegt schon einer vor, gilt dessen ID. Das Fenster führt den
      // Vorgang; bis `sendetBis` kann kein anderes Fenster ihn übernehmen oder verwerfen.
      d = await sichereRetoureVorgang(geraeteSpeicher, {
        vorgangId,
        kassenId,
        angelegtAm: new Date().toISOString(),
        anfrage,
        geraetekennung: kennung,
        phase: 'gesendet',
        fenster,
        sendetBis: Date.now() + SENDE_FRIST_MS,
      });
    } catch {
      setFehler('Gerätespeicher nicht verfügbar – Retoure nicht gesendet, nichts auszahlen.');
      sendetRef.current = false;
      setSendet(false);
      return;
    }
    // ab jetzt gilt der gesicherte Vorgang (der Entwurf ist darin enthalten)
    await entferneRetoureEntwurf(geraeteSpeicher, kassenId).catch(() => undefined);
    if (d.vorgangId !== vorgangId || d.phase !== 'gesendet' || d.fenster !== fenster) {
      // anderer offener Vorgang auf dem Gerät (zweites Fenster): anzeigen bzw. klären, nicht senden
      setVorgang(d);
      return;
    }
    // Erst nach der Antwort (bzw. bei unklarem Ausgang) wechselt die Ansicht: Solange der Abschluss
    // läuft, gibt es keine Standabfrage und kein „Erneut senden“ (Klärung nur bei offenem Ausgang)
    await sende(d);
  }

  /* ── Klärung ─────────────────────────────────────────────────────────────────────── */
  const [prueft, setPrueft] = useState(false);
  const pruefeStand = useCallback(
    async (d: RetoureVorgangDatensatz): Promise<void> => {
      setPrueft(true);
      try {
        await verarbeite(d, await api.stand(kassenId, d.vorgangId));
      } catch {
        setKlaerung('Stand nicht abrufbar – Verbindung prüfen und erneut prüfen.');
      } finally {
        setPrueft(false);
      }
    },
    [api, kassenId, verarbeite],
  );
  // Vorgang eines anderen Fensters: nur anzeigen (Codex R1 K1/K2)
  const fremd =
    vorgang !== 'laedt' && vorgang?.fenster !== undefined && vorgang.fenster !== fenster;
  // Abgleich zwischen Fenstern (Besitz, Auszahlung, Ende), solange ein Vorgang offen ist
  const offen = vorgang !== 'laedt' && vorgang !== null;
  useEffect(() => {
    if (!offen) {
      return;
    }
    const t = setInterval(() => {
      void ladeRetoureVorgang(geraeteSpeicher, kassenId)
        .then((d) => {
          setVorgang((alt) => (JSON.stringify(alt) === JSON.stringify(d) ? alt : d));
        })
        .catch(() => undefined);
    }, ABGLEICH_MS);
    return () => {
      clearInterval(t);
    };
  }, [offen, geraeteSpeicher, kassenId]);

  // Offener Vorgang nach Neuladen bzw. beim Öffnen: einmal den Stand abfragen (nur lesend)
  const erstePruefung = useRef<string | null>(null);
  useEffect(() => {
    if (vorgang === 'laedt' || vorgang?.phase !== 'gesendet' || !online || fremd) {
      return;
    }
    if (erstePruefung.current === vorgang.vorgangId) {
      return;
    }
    erstePruefung.current = vorgang.vorgangId;
    setKlaerung((k) => k ?? 'Offene Retoure gefunden – Stand wird geprüft.');
    void pruefeStand(vorgang);
  }, [vorgang, online, fremd, pruefeStand]);

  const erneutSendenRef = useRef(false);
  async function erneutSenden(d: RetoureVorgangDatensatz): Promise<void> {
    if (erneutSendenRef.current || !online || Date.now() < (d.sendetBis ?? 0)) {
      return;
    }
    erneutSendenRef.current = true;
    setPrueft(true);
    try {
      // Besitz prüfen und Frist setzen, erst dann senden – dieselbe Vorgangs-ID, dieselben Nutzdaten
      const neu = await aktualisiereRetoureVorgang(
        geraeteSpeicher,
        d.kassenId,
        d.vorgangId,
        { sendetBis: Date.now() + SENDE_FRIST_MS },
        fenster,
      ).catch(() => null);
      if (neu === null) {
        await neuLesen();
        return;
      }
      setVorgang(neu);
      await sende(neu);
    } finally {
      erneutSendenRef.current = false;
      setPrueft(false);
    }
  }

  async function verwerfen(d: RetoureVorgangDatensatz): Promise<void> {
    if (Date.now() < (d.sendetBis ?? 0)) {
      return;
    }
    const weg = await entferneRetoureVorgang(
      geraeteSpeicher,
      d.kassenId,
      d.vorgangId,
      fenster,
    ).catch(() => false);
    if (!weg) {
      await neuLesen();
      return;
    }
    setVorgang(null);
    setKlaerung(null);
    sendetRef.current = false;
    setSendet(false);
    neueRetoure();
  }

  // Esc = Abbrechen (Figma RT2-2), nie während des Abschlusses, bei offenem Dialog oder Klärung;
  // ein Esc, das die Bildschirmtastatur schon geschlossen hat, zählt nicht
  const onFertig = props.onFertig;
  const escErlaubt = !sendet && freigabeDialog === null && vorgang === null;
  useEffect(() => {
    if (!escErlaubt) {
      return;
    }
    const taste = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape' && !ev.defaultPrevented && !istTastaturEsc(ev)) {
        onFertig();
      }
    };
    window.addEventListener('keydown', taste);
    return () => {
      window.removeEventListener('keydown', taste);
    };
  }, [escErlaubt, onFertig]);

  /* ── Darstellung ─────────────────────────────────────────────────────────────────── */
  if (vorgang === 'laedt') {
    return (
      <div className="retoure">
        <p className="hinweis">Retoure wird geladen …</p>
      </div>
    );
  }

  if (fremd) {
    const frei = Date.now() >= (vorgang.sendetBis ?? 0);
    return (
      <div className="retoure">
        <div className="karte rt-klaerung" role="alert">
          <h2>Retoure läuft in einem anderen Fenster</h2>
          <p>
            Diese Retoure wird in einem anderen Fenster dieser Kasse bearbeitet. Dort weiterarbeiten
            – hier <strong>nichts auszahlen</strong>.
          </p>
          <dl className="rt-uebersicht">
            <dt>Vorgang</dt>
            <dd>
              <code>{vorgang.vorgangId}</code>
            </dd>
            <dt>Original</dt>
            <dd>
              {vorgang.anfrage.original.belegnummer} (Kasse {vorgang.anfrage.original.kassenId})
            </dd>
            <dt>Stand</dt>
            <dd>
              {vorgang.phase === 'gesendet'
                ? 'Abschluss offen bzw. in Klärung'
                : `Retourenbeleg ${vorgang.ergebnis?.beleg.belegnummer ?? ''} signiert · Auszahlung ${
                    vorgang.auszahlungBestaetigt === true ? 'bestätigt' : 'offen'
                  }`}
            </dd>
          </dl>
          <p className="hinweis">
            Nur übernehmen, wenn das andere Fenster geschlossen ist. Eine dort schon geleistete
            Auszahlung nicht wiederholen.
          </p>
          <div className="rt-aktionen-zeile">
            <button
              type="button"
              className="taste zweit gross"
              disabled={!frei}
              onClick={() => {
                void uebernimmRetoureVorgang(
                  geraeteSpeicher,
                  kassenId,
                  vorgang.vorgangId,
                  fenster,
                  Date.now(),
                )
                  .catch(() => null)
                  .then((d) => {
                    if (d === null) {
                      void neuLesen();
                    } else {
                      erstePruefung.current = null;
                      setVorgang(d);
                    }
                  });
              }}
            >
              {frei ? 'Hier übernehmen' : 'Abschluss läuft noch – Übernehmen gesperrt'}
            </button>
            <button type="button" className="taste zweit gross" onClick={props.onFertig}>
              Zurück zum Verkauf
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (vorgang?.phase === 'abgeschlossen' && vorgang.ergebnis !== undefined) {
    return (
      <RetoureErgebnisAnsicht
        api={api}
        vorgang={{ ...vorgang, ergebnis: vorgang.ergebnis }}
        geraeteSpeicher={geraeteSpeicher}
        fenster={fenster}
        {...(props.bridgeDruck === undefined ? {} : { bridgeDruck: props.bridgeDruck })}
        {...(props.ladeOeffnen === undefined ? {} : { ladeOeffnen: props.ladeOeffnen })}
        onVerloren={() => {
          void neuLesen();
        }}
        standPruefen={ausSpeicher}
        onZurueck={props.onFertig}
        onFertig={() => {
          void entferneRetoureVorgang(geraeteSpeicher, vorgang.kassenId, vorgang.vorgangId, fenster)
            .catch(() => false)
            .then((weg) => {
              if (weg) {
                props.onFertig();
              } else {
                void neuLesen();
              }
            });
        }}
      />
    );
  }

  if (vorgang !== null && vorgang.phase === 'gesendet') {
    const unbekannt = klaerung?.startsWith('Der Server kennt diese Retoure nicht') === true;
    // solange ein Abschluss noch unterwegs sein kann, weder erneut senden noch abbrechen
    const nachlaufBis = vorgang.sendetBis ?? 0;
    const imNachlauf = Date.now() < nachlaufBis;
    return (
      <div className="retoure">
        <div className="karte rt-klaerung" role="alert" aria-live="assertive">
          <h2>{KLAERUNG_TEXT}</h2>
          <p>
            Nichts auszahlen und die Retoure nicht neu beginnen, bis der Stand geklärt ist. Die
            Abfrage nutzt genau diese Vorgangs-ID.
          </p>
          <dl className="rt-uebersicht">
            <dt>Vorgang</dt>
            <dd>
              <code>{vorgang.vorgangId}</code>
            </dd>
            <dt>Original</dt>
            <dd>
              {vorgang.anfrage.original.belegnummer} (Kasse {vorgang.anfrage.original.kassenId})
            </dd>
            <dt>Gesendet</dt>
            <dd>{datum(vorgang.angelegtAm)}</dd>
          </dl>
          {klaerung !== null && <p className="warnung">{klaerung}</p>}
          {unbekannt && imNachlauf && (
            <p className="hinweis">
              Ein Abschluss kann noch unterwegs sein – ab{' '}
              {new Date(nachlaufBis).toLocaleTimeString('de-AT', { timeZone: 'Europe/Vienna' })}{' '}
              erneut prüfen.
            </p>
          )}
          {!online && <p className="warnung">{OFFLINE_TEXT} – Abfrage nach Wiederverbindung.</p>}
          <div className="rt-aktionen-zeile">
            <button
              type="button"
              className="taste gross"
              disabled={prueft || !online}
              onClick={() => {
                void pruefeStand(vorgang);
              }}
            >
              {prueft ? 'Wird geprüft …' : 'Jetzt prüfen'}
            </button>
            {unbekannt && !imNachlauf && (
              <>
                <button
                  type="button"
                  className="taste zweit gross"
                  disabled={prueft || !online}
                  onClick={() => {
                    void erneutSenden(vorgang);
                  }}
                >
                  Erneut senden (gleiche Vorgangs-ID)
                </button>
                <button
                  type="button"
                  className="taste zweit gross"
                  disabled={prueft}
                  onClick={() => {
                    void verwerfen(vorgang);
                  }}
                >
                  Retoure abbrechen
                </button>
              </>
            )}
            <button type="button" className="taste zweit gross" onClick={props.onFertig}>
              Zurück zum Verkauf
            </button>
          </div>
        </div>
      </div>
    );
  }

  const summeCent = aktuell?.summeCent ?? null;
  const belegFeld = bt.feld('beleg', {
    name: 'Belegnummer',
    setzen: setEingabe,
    bestaetigen: {
      text: 'Suchen',
      aktion: () => {
        void suche('nummer');
      },
    },
  });
  const sperrenTasten = gesperrt || sendet || speicherFehler;

  /** Fehler- und Wiederholungsfälle oben im Inhalt, mit nächster Aktion (Nutzer 04.11.2026, 8). */
  const ersterMengenfehler =
    schritt === 'artikel'
      ? positionen
          .map((p) => ({ p, f: abgewiesen.get(p.index) ?? mengenFehler.get(p.index) }))
          .find((x) => x.f !== undefined)
      : undefined;
  const mengenMeldung: MeldungInhalt | null =
    ersterMengenfehler?.f === undefined
      ? null
      : {
          text: `${ersterMengenfehler.p.bezeichnung}: ${ersterMengenfehler.f}`,
          aktion: {
            text: 'Menge korrigieren',
            tun: () => {
              const z = `table.rt-positionen tr[data-index="${String(ersterMengenfehler.p.index)}"]`;
              (
                document.querySelector<HTMLElement>(`${z} input:not(:disabled)`) ??
                document.querySelector<HTMLElement>(`${z} button:not(:disabled)`)
              )?.focus();
            },
          },
        };
  const meldung: MeldungInhalt | null =
    fehler !== null
      ? { text: fehler }
      : vorschauFehler !== null && schritt !== 'beleg' && schritt !== 'artikel'
        ? {
            text: vorschauFehler,
            aktion: {
              text: 'Grund und Zustand prüfen',
              tun: () => {
                setSchritt('grund');
              },
            },
          }
        : (mengenMeldung ??
          (suchMeldung !== null && schritt === 'beleg' && original === null
            ? {
                text: `${suchMeldung.feld === 'qr' ? 'Bon-QR' : 'Belegnummer'}: ${suchMeldung.text}`,
                aktion: {
                  text: suchMeldung.feld === 'qr' ? 'Erneut scannen' : 'Belegnummer prüfen',
                  tun: () => {
                    (suchMeldung.feld === 'qr' ? suchfeld : nummerFeld).current?.focus();
                  },
                },
              }
            : null));

  return (
    <div className="retoure">
      <nav className="rt-schritte" aria-label="Schritte der Retoure">
        <ol>
          {SCHRITTE.map((s, i) => {
            const erledigt = i < stelle;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  className={i === stelle ? 'aktiv' : erledigt ? 'erledigt' : ''}
                  aria-current={i === stelle ? 'step' : undefined}
                  disabled={!erledigt || sendet}
                  onClick={() => {
                    setSchritt(s.id);
                  }}
                >
                  {erledigt ? '✓' : String(i + 1)} {s.text}
                </button>
              </li>
            );
          })}
          <li>
            <button type="button" disabled>
              {String(SCHRITTE.length + 1)} Abschluss
            </button>
          </li>
        </ol>
        <span className="rt-entwurf-marke">Stufe A · produktive Aktivierung nicht freigegeben</span>
        <button type="button" className="taste zweit" disabled={sendet} onClick={props.onFertig}>
          Abbrechen (Esc)
        </button>
      </nav>

      {sperre !== null && (
        <div className="karte rt-sperre" role="alert">
          {sperre}
        </div>
      )}
      {hinweis !== null && (
        <p className="hinweis" role="status">
          {hinweis}
        </p>
      )}

      <div className="rt-raster">
        <section className="karte rt-arbeit" aria-labelledby="rt-ueberschrift">
          {meldung !== null && <Meldung id="rt-such-meldung" inhalt={meldung} />}
          {schritt === 'beleg' && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                {original === null ? 'Originalbeleg suchen oder scannen' : 'Originalbeleg prüfen'}
              </h2>
              {original === null ? (
                <>
                  <div className="rt-suche-wege">
                    <div className="rt-suche rt-weg">
                      <label htmlFor="rt-qr">Bon-QR scannen</label>
                      <input
                        id="rt-qr"
                        value={qrEingabe}
                        disabled={sperrenTasten}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="Handscanner auf den QR-Code richten"
                        aria-invalid={suchMeldung?.feld === 'qr'}
                        aria-describedby={
                          suchMeldung?.feld === 'qr' ? 'rt-such-meldung' : undefined
                        }
                        // eigene Scanerkennung: Der QR-Datenblock samt Enter gehört diesem Feld
                        data-eigene-scanerkennung=""
                        onChange={(ev) => {
                          setQrEingabe(ev.target.value);
                          setHinweis(null);
                        }}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter') {
                            ev.preventDefault();
                            void suche('qr');
                          }
                        }}
                        ref={suchfeld}
                      />
                      <p className="hinweis">Der Scan sucht sofort (Enter des Scanners).</p>
                    </div>
                    <div className="rt-suche rt-weg">
                      <label htmlFor="rt-nummer">Belegnummer eingeben</label>
                      <span className="feld-mit-knopf">
                        <input
                          id="rt-nummer"
                          value={eingabe}
                          disabled={sperrenTasten}
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="z. B. B-20261104-T3-0042"
                          aria-invalid={suchMeldung?.feld === 'nummer'}
                          aria-describedby={
                            suchMeldung?.feld === 'nummer' ? 'rt-such-meldung' : undefined
                          }
                          data-scansperre="Belegnummer"
                          onChange={(ev) => {
                            setEingabe(ev.target.value);
                            setHinweis(null);
                          }}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter') {
                              ev.preventDefault();
                              void suche('nummer');
                            }
                          }}
                          {...belegFeld}
                          ref={(el) => {
                            nummerFeld.current = el;
                            belegFeld.ref(el);
                          }}
                        />
                        <TastaturKnopf steuerung={bt.knopf('beleg')} />
                      </span>
                      <button
                        type="button"
                        className="taste gross"
                        disabled={sperrenTasten || sucht}
                        onClick={() => {
                          void suche('nummer');
                        }}
                      >
                        {sucht ? 'Sucht …' : 'Suchen'}
                      </button>
                    </div>
                  </div>
                  {bt.tastatur !== null && <Bildschirmtastatur steuerung={bt.tastatur} />}
                  {mehrdeutig !== null && (
                    <div className="chips" role="group" aria-label="Kasse des Originalbelegs">
                      {mehrdeutig.kassen.map((k) => (
                        <button
                          key={k}
                          type="button"
                          disabled={sperrenTasten || sucht}
                          onClick={() => {
                            void suche('nummer', k);
                          }}
                        >
                          Kasse {k}
                        </button>
                      ))}
                    </div>
                  )}
                  <p className="hinweis rt-punkt">
                    Gesucht wird in allen Kassen des Unternehmens. Rückgabe nur mit Originalbeleg,
                    nur online. Der Originalbeleg bleibt unverändert; die Rückgabe bekommt einen
                    eigenen, signierten Beleg.
                  </p>
                </>
              ) : (
                <OriginalPruefen original={original} />
              )}
            </>
          )}

          {schritt === 'artikel' && original !== null && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Artikel auswählen
              </h2>
              <div className="rt-aktionen-zeile">
                <button
                  type="button"
                  className="taste zweit"
                  disabled={sperrenTasten}
                  onClick={allesZurueck}
                >
                  Alles zurück
                </button>
              </div>
              <PositionsAuswahl
                positionen={positionen}
                auswahl={auswahl}
                vorschau={aktuell}
                mengenFehler={mengenFehler}
                abgewiesen={abgewiesen}
                onAbweisung={abweisung}
                gesperrt={sperrenTasten}
                onWahl={setzeWahl}
              />
              <p className="hinweis rt-punkt">
                Beträge berechnet der Server aus dem Originalbeleg (Preis, Rabatte, Steuersatz),
                nicht aus dem aktuellen Artikelstamm – sie erscheinen nach Grund und Zustand.
              </p>
            </>
          )}

          {schritt === 'grund' && original !== null && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Grund und Zustand
              </h2>
              <fieldset className="rt-angaben">
                <legend>Rückgabegrund (Pflicht, für den ganzen Vorgang)</legend>
                <div className="chips" role="group" aria-label="Rückgabegrund">
                  {GRUENDE.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      className={grund === g.id ? 'aktiv' : ''}
                      aria-pressed={grund === g.id}
                      disabled={sperrenTasten}
                      onClick={() => {
                        waehleGrund(g.id);
                      }}
                    >
                      {g.text}
                    </button>
                  ))}
                </div>
                <label>
                  Text {grund === 'sonstiges' ? '(Pflicht bei „Sonstiges“)' : '(optional)'} ·{' '}
                  {grundText.length}/{GRUND_TEXT_MAX}
                  <input
                    value={grundText}
                    maxLength={GRUND_TEXT_MAX}
                    disabled={sperrenTasten}
                    data-scansperre="Rückgabegrund"
                    onChange={(ev) => {
                      setGrundText(ev.target.value);
                    }}
                    {...bt.feld('grund', { name: 'Rückgabegrund', setzen: setGrundText })}
                  />
                </label>
                <TastaturKnopf
                  steuerung={bt.knopf('grund')}
                  klein
                  beschriftung="Tastatur für den Grund"
                />
                {bt.tastatur !== null && <Bildschirmtastatur steuerung={bt.tastatur} />}
                {grund === 'umtausch' && (
                  <p className="hinweis rt-punkt">
                    Umtausch: Die Ware wird zurückgenommen und bar erstattet; den neuen Artikel
                    danach normal verkaufen.
                  </p>
                )}
              </fieldset>
              {gewaehlt.map(([index, w]) => {
                const p = posVon(index);
                const id = `rt-zustand-${String(index)}`;
                return (
                  <fieldset key={index} className="rt-angaben" aria-labelledby={id}>
                    <h3 id={id}>
                      {p?.bezeichnung ?? 'Position'} · {mengeText(w.menge, p?.einheit ?? 'stk')}
                    </h3>
                    <div className="chips rt-segmente" role="radiogroup" aria-label="Zustand">
                      {ZUSTAENDE.map((z) => (
                        <button
                          key={z.id}
                          type="button"
                          role="radio"
                          aria-checked={w.zustand === z.id}
                          className={w.zustand === z.id ? 'aktiv' : ''}
                          disabled={sperrenTasten}
                          onClick={() => {
                            setzeWahl(index, { zustand: z.id });
                          }}
                        >
                          {z.text}
                        </button>
                      ))}
                    </div>
                  </fieldset>
                );
              })}
              {(grundFehlt !== null || zustandFehlt.length > 0) && (
                <ul className="hinweis">
                  {[...(grundFehlt === null ? [] : [grundFehlt]), ...zustandFehlt].map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              )}
            </>
          )}

          {schritt === 'erstattung' && original !== null && (
            <>
              <h2 id="rt-ueberschrift" ref={ueberschrift} tabIndex={-1}>
                Zusammenfassung und Erstattung
              </h2>
              {aktuell === null ? (
                <p className="hinweis">
                  {vorschauLaedt ? 'Vorschau wird berechnet …' : 'Keine Vorschau.'}
                </p>
              ) : (
                <>
                  <BetragsTabellen vorschau={aktuell} positionen={positionen} />
                  <h3>Erstattungsart</h3>
                  <div className="rt-arten" role="radiogroup" aria-label="Erstattungsart">
                    {(['bar', 'karte', 'gutschein', 'gemischt'] as const).map((a) => {
                      const erlaubt = a === 'bar' && barErlaubt;
                      const grundAus =
                        a === 'bar'
                          ? erlaubt
                            ? null
                            : (original.erstattungSperre ?? 'Barerstattung nicht zugelassen')
                          : (original.erstattungSperre ?? KARTE_GESPERRT_TEXT);
                      return (
                        <label
                          key={a}
                          className={`rt-art${erlaubt ? '' : ' rt-aus'}${art === a ? ' gewaehlt' : ''}`}
                        >
                          <input
                            type="radio"
                            name="rt-art"
                            checked={art === a}
                            disabled={!erlaubt || sperrenTasten}
                            onChange={() => {
                              setArt(a);
                            }}
                          />
                          <strong>{ART_TEXT[a]}</strong>
                          {grundAus !== null && <small>{grundAus}</small>}
                        </label>
                      );
                    })}
                  </div>
                  {aktuell.freigabeNoetig ? (
                    <div className="rt-kasten">
                      <strong>Freigabe nötig</strong>
                      <p>
                        Erstattung
                        {aktuell.freigabeAbCent === null
                          ? ' über der Grenze'
                          : ` über ${euro(aktuell.freigabeAbCent)}`}{' '}
                        – Freigabe-PIN einer berechtigten Person (nicht die eigene).
                      </p>
                      <p className="hinweis">
                        Freigabe wird protokolliert. Selbstfreigabe ist gesperrt.
                      </p>
                      {!freigabeGilt ? (
                        <button
                          type="button"
                          className="taste zweit gross"
                          disabled={sperrenTasten || freigabeLaeuft}
                          onClick={() => {
                            void freigabeHolen();
                          }}
                        >
                          {freigabeLaeuft ? 'Freigabe wird geprüft …' : 'Freigabe-PIN eingeben'}
                        </button>
                      ) : (
                        <p className="ok">
                          Freigegeben durch {freigabe.name}{' '}
                          <button
                            type="button"
                            className="taste zweit"
                            disabled={sperrenTasten}
                            onClick={() => {
                              setFreigabe(null);
                            }}
                          >
                            Ändern
                          </button>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="hinweis rt-punkt">Keine Freigabe nötig.</p>
                  )}
                </>
              )}
            </>
          )}
        </section>

        <aside className="rt-seite" aria-label="Zusammenfassung der Rückgabe">
          <div className="karte rt-zusammenfassung">
            <div className="rt-betragsband">
              <span>Erstattung{art === null ? '' : ` ${ART_TEXT[art].toLowerCase()}`}</span>
              <strong aria-live="polite">
                {summeCent === null ? (vorschauLaedt ? '…' : '—') : euro(summeCent)}
              </strong>
            </div>
            <div className="rt-zusammenfassung-inhalt">
              <h3>Rückgabe</h3>
              {original === null ? (
                <p className="hinweis">Noch kein Originalbeleg gewählt.</p>
              ) : (
                <>
                  <p className="hinweis">zu {original.belegnummer}</p>
                  {aktuell !== null && (
                    <>
                      <ul className="rt-zeilen">
                        {aktuell.zeilen.map((z) => {
                          const p = posVon(z.index);
                          return (
                            <li key={z.index}>
                              <span>
                                {z.bezeichnung}
                                <small>{mengeText(z.menge, p?.einheit ?? 'stk')}</small>
                              </span>
                              <strong className="rt-negativ">{euro(-z.erstattungCent)}</strong>
                            </li>
                          );
                        })}
                      </ul>
                      <ul className="rt-zeilen rt-saetze-kurz" aria-label="Je Steuersatz">
                        {aktuell.jeSteuersatz.map((s) => (
                          <li key={s.steuersatz}>
                            <span>
                              {s.steuersatz} %
                              <small>
                                netto {euro(s.nettoCent)} · USt {euro(s.ustCent)}
                              </small>
                            </span>
                            <strong>{euro(s.bruttoCent)}</strong>
                          </li>
                        ))}
                        {aktuell.rabattCent !== 0 && (
                          <li>
                            <span>davon anteiliger Bon-Rabatt</span>
                            <strong>{euro(aktuell.rabattCent)}</strong>
                          </li>
                        )}
                      </ul>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="rt-aktionen">
            {schritt === 'erstattung' ? (
              <button
                type="button"
                className="taste gross rt-abschluss"
                disabled={
                  sperrenTasten ||
                  aktuell === null ||
                  art !== 'bar' ||
                  !barErlaubt ||
                  (aktuell.freigabeNoetig && !freigabeGilt)
                }
                onClick={() => {
                  void bestaetigen();
                }}
              >
                {sendet
                  ? 'Rückgabe wird bestätigt …'
                  : `Rückgabe bestätigen${summeCent === null || art === null ? '' : ` – ${euro(summeCent)} ${ART_TEXT[art].toLowerCase()}`}`}
              </button>
            ) : (
              <button
                type="button"
                className="taste gross"
                disabled={sperrenTasten || !weiterMoeglich[schritt]}
                onClick={weiter}
              >
                {schritt === 'beleg'
                  ? 'Weiter: Artikel'
                  : schritt === 'artikel'
                    ? 'Weiter: Grund und Zustand'
                    : 'Weiter: Erstattung'}
              </button>
            )}
            <button type="button" className="taste zweit gross" disabled={sendet} onClick={zurueck}>
              {schritt === 'beleg' && original === null ? 'Zurück zum Verkauf' : 'Zurück'}
            </button>
          </div>
        </aside>
      </div>

      {freigabeDialog !== null && (
        <BedienerAnmeldung
          bediener={freigabeDialog}
          laeuft={false}
          fehler={null}
          hinweis="Freigabe durch eine berechtigte Person"
          onAbbrechen={() => {
            setFreigabeDialog(null);
          }}
          onAnmelden={(b, pin) => {
            setFreigabeDialog(null);
            if (pin === undefined || pin === '') {
              setFehler('Freigabe nur mit PIN möglich.');
              return;
            }
            // Die PIN geht nur an die Freigabe-Route und wird nicht gespeichert
            void freigabePruefen(b.id, pin);
          }}
        />
      )}
    </div>
  );
}

interface MeldungInhalt {
  text: string;
  aktion?: { text: string; tun: () => void };
}

/** Meldung oben im Inhalt: Icon, Text, nächste Aktion. */
function Meldung(props: { id?: string; inhalt: MeldungInhalt; art?: 'fehler' | 'warnung' }) {
  const { inhalt } = props;
  return (
    <div
      id={props.id}
      className={`rt-meldung ${props.art === 'warnung' ? 'rt-meldung-warnung' : 'rt-meldung-fehler'}`}
      role="alert"
    >
      <span className="rt-meldung-icon" aria-hidden="true">
        ⚠
      </span>
      <p>{inhalt.text}</p>
      {inhalt.aktion !== undefined && (
        <button type="button" className="taste zweit" onClick={inhalt.aktion.tun}>
          {inhalt.aktion.text}
        </button>
      )}
    </div>
  );
}

function OriginalPruefen(props: { original: RetoureOriginal }) {
  const o = props.original;
  const kulanz = kulanzHinweis(o.zeitpunkt, Date.now());
  return (
    <div className="rt-kasten">
      <dl className="rt-uebersicht">
        <dt>Beleg</dt>
        <dd>{o.belegnummer}</dd>
        <dt>Kasse</dt>
        <dd>{o.kassenId}</dd>
        <dt>Datum</dt>
        <dd>
          {datum(o.zeitpunkt)}
          {kulanz !== null && (
            <>
              {' '}
              <strong className="rt-kulanz">{kulanz}</strong>
            </>
          )}
        </dd>
        <dt>Zahlungsart</dt>
        <dd>{o.zahlungsart ?? '–'}</dd>
        <dt>Summe</dt>
        <dd>{euro(o.summeCent)}</dd>
        <dt>Positionen</dt>
        <dd>{String(o.positionen.length)}</dd>
      </dl>
      {o.retouren.length > 0 && (
        <ul className="rt-zeilen" aria-label="Bisherige Rückgaben">
          {o.retouren.map((x) => (
            <li key={x.belegnummer}>
              <span>
                Bereits zurückgegeben: {x.belegnummer}
                <small>
                  Kasse {x.kassenId} · {datum(x.zeitpunkt)}
                </small>
              </span>
              <strong>{euro(x.summeCent)}</strong>
            </li>
          ))}
        </ul>
      )}
      {o.vollstaendigZurueck ? (
        <p className="fehler" role="alert">
          Dieser Beleg wurde bereits vollständig zurückgegeben.
        </p>
      ) : o.rueckgabefaehig ? (
        <p className="ok">Original geprüft – Rückgabe möglich.</p>
      ) : (
        <ul className="fehler" role="alert">
          {(o.gruende.length > 0 ? o.gruende : ['Beleg ist nicht retournierbar.']).map((g) => (
            <li key={g}>{g}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PositionsAuswahl(props: {
  positionen: readonly RetourePosition[];
  auswahl: ReadonlyMap<number, Wahl>;
  vorschau: RetoureVorschau | null;
  mengenFehler: ReadonlyMap<number, string>;
  /** Sofort abgewiesene Eingaben über „noch möglich“ (Grenze vom Server), je Zeile. */
  abgewiesen: ReadonlyMap<number, string>;
  onAbweisung: (index: number, text: string | null) => void;
  gesperrt: boolean;
  onWahl: (index: number, teil: Partial<Wahl>) => void;
}) {
  const { positionen, auswahl, vorschau, mengenFehler, abgewiesen, gesperrt, onWahl } = props;
  const abweisung = props.onAbweisung;
  return (
    <div className="rt-tabelle">
      <table className="liste rt-positionen">
        <caption className="unsichtbar">Positionen des Originalbelegs</caption>
        <thead>
          <tr>
            <th>Artikel</th>
            <th className="betrag rt-spalte-menge">verkauft</th>
            <th className="betrag rt-spalte-menge">bereits zurück</th>
            <th className="betrag rt-spalte-menge">noch möglich</th>
            <th>Rückgabe</th>
            <th className="betrag rt-betrag">Betrag</th>
          </tr>
        </thead>
        <tbody>
          {positionen.map((p) => {
            const w = auswahl.get(p.index);
            const menge = w?.menge ?? 0;
            const aus = gesperrt || p.sperrgrund !== null || tausendstel(p.moeglich) <= 0;
            const zeile = vorschau?.zeilen.find((z) => z.index === p.index);
            const f = abgewiesen.get(p.index) ?? mengenFehler.get(p.index);
            const e = p.einheit;
            return (
              <tr
                key={p.index}
                data-index={p.index}
                className={p.sperrgrund !== null || tausendstel(p.moeglich) <= 0 ? 'rt-aus' : ''}
              >
                <td className="rt-bezeichnung">
                  <strong>{p.bezeichnung}</strong>
                  <small>
                    {p.sperrgrund ??
                      (tausendstel(p.bisherZurueck) > 0
                        ? `${mengeText(p.bisherZurueck, e)} von ${mengeText(p.gekauft, e)} bereits zurück`
                        : `${p.steuersatz} %`)}
                  </small>
                  <small className="rt-nur-schmal">
                    verkauft {mengeText(p.gekauft, e)} · bereits zurück{' '}
                    {mengeText(p.bisherZurueck, e)} · noch möglich {mengeText(p.moeglich, e)}
                  </small>
                  {f !== undefined && (
                    <small className="fehler" role="alert">
                      {f}
                    </small>
                  )}
                </td>
                <td className="betrag rt-spalte-menge">{mengeText(p.gekauft, e)}</td>
                <td className="betrag rt-spalte-menge">{mengeText(p.bisherZurueck, e)}</td>
                <td className="betrag rt-spalte-menge">{mengeText(p.moeglich, e)}</td>
                <td>
                  <span className="stepper rt-stepper">
                    <button
                      type="button"
                      aria-label={`${p.bezeichnung}: weniger`}
                      disabled={aus || tausendstel(menge) <= 0}
                      onClick={() => {
                        // nurGanz: nur ganz oder gar nicht – „−“ wählt die Zeile ab (Codex R1 M7)
                        abweisung(p.index, null);
                        onWahl(p.index, {
                          menge: p.nurGanz ? 0 : schrittMenge(menge, -1, p.moeglich, e),
                          eingabe: null,
                        });
                      }}
                    >
                      −
                    </button>
                    {e === 'kg' && !p.nurGanz ? (
                      <input
                        className="rt-menge-feld"
                        inputMode="decimal"
                        aria-label={`${p.bezeichnung}: Menge in kg`}
                        disabled={aus}
                        value={
                          w?.eingabe ??
                          (tausendstel(menge) > 0 ? mengeText(menge, 'kg').replace(' kg', '') : '')
                        }
                        placeholder="0,000"
                        data-scansperre="Rückgabemenge"
                        onChange={(ev) => {
                          const roh = ev.target.value;
                          const r = leseMenge(roh, 'kg');
                          // über „noch möglich“: sofort abweisen, die Menge bleibt (Nutzer, 2)
                          const zuHoch = r.ok ? mengeFehler(r.menge, p.moeglich, 'kg') : null;
                          if (zuHoch !== null) {
                            abweisung(p.index, zuHoch);
                            return;
                          }
                          abweisung(p.index, null);
                          onWahl(p.index, {
                            eingabe: roh === '' ? null : roh,
                            menge: r.ok ? r.menge : 0,
                          });
                        }}
                      />
                    ) : (
                      <span aria-live="polite">
                        {mengeText(menge, e)} von {mengeText(p.moeglich, e)}
                      </span>
                    )}
                    <button
                      type="button"
                      aria-label={`${p.bezeichnung}: mehr`}
                      disabled={aus || tausendstel(menge) >= tausendstel(p.moeglich)}
                      onClick={() => {
                        abweisung(p.index, null);
                        onWahl(p.index, {
                          menge: p.nurGanz ? p.moeglich : schrittMenge(menge, 1, p.moeglich, e),
                          eingabe: null,
                        });
                      }}
                    >
                      +
                    </button>
                  </span>
                  <button
                    type="button"
                    className="taste zweit rt-rest"
                    aria-label={`${p.bezeichnung}: Rest übernehmen`}
                    disabled={aus || tausendstel(menge) >= tausendstel(p.moeglich)}
                    onClick={() => {
                      abweisung(p.index, null);
                      onWahl(p.index, { menge: p.moeglich, eingabe: null });
                    }}
                  >
                    Rest übernehmen
                  </button>
                </td>
                <td className="betrag rt-betrag">
                  {zeile === undefined ? (
                    '–'
                  ) : (
                    <span className="rt-negativ">{euro(-zeile.erstattungCent)}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BetragsTabellen(props: {
  vorschau: RetoureVorschau;
  positionen: readonly RetourePosition[];
}) {
  const v = props.vorschau;
  return (
    <div className="rt-kasten">
      <table className="liste">
        <caption>Erstattung laut Server (aus dem Originalbeleg)</caption>
        <tbody>
          {v.zeilen.map((z) => {
            const p = props.positionen.find((x) => x.index === z.index);
            return (
              <tr key={z.index}>
                <td>
                  {z.bezeichnung} · {mengeText(z.menge, p?.einheit ?? 'stk')}
                </td>
                <td className="betrag">{euro(z.erstattungCent)}</td>
              </tr>
            );
          })}
          {v.rabattCent !== 0 && (
            <tr>
              <td>davon anteiliger Bon-Rabatt</td>
              <td className="betrag">{euro(v.rabattCent)}</td>
            </tr>
          )}
          <tr className="rt-gesamt">
            <th scope="row">Erstattung</th>
            <td className="betrag">{euro(v.summeCent)}</td>
          </tr>
        </tbody>
      </table>
      <table className="liste rt-saetze">
        <caption>Je Steuersatz</caption>
        <thead>
          <tr>
            <th>Satz</th>
            <th className="betrag">Brutto</th>
            <th className="betrag">Netto</th>
            <th className="betrag">USt</th>
          </tr>
        </thead>
        <tbody>
          {v.jeSteuersatz.map((s) => (
            <tr key={s.steuersatz}>
              <td>{s.steuersatz} %</td>
              <td className="betrag">{euro(s.bruttoCent)}</td>
              <td className="betrag">{euro(s.nettoCent)}</td>
              <td className="betrag">{euro(s.ustCent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Ergebnis (RT2-6a/6b): Auszahlungsbetrag groß, Lade (nur bar, einmal), Retourenbeleg mit
 * „RÜCKGABE“ und Bezug über den vorhandenen Druckweg bzw. als Bildschirmbeleg.
 */
function RetoureErgebnisAnsicht(props: {
  api: RetoureApi;
  vorgang: RetoureVorgangDatensatz & { ergebnis: RetoureAbschluss };
  geraeteSpeicher: GeraeteSpeicher;
  /** Dieses Fenster (Besitz des Vorgangs wird bei jeder Aktion geprüft). */
  fenster: string;
  bridgeDruck?: BonDruck;
  ladeOeffnen?: (belegnummer: string) => Promise<GeraeteRueckmeldung>;
  /** Der Vorgang gehört nicht mehr diesem Fenster bzw. ist weg: neu lesen. */
  onVerloren: () => void;
  /** Nach Neuladen: Auszahlungsstand über die Vorgangs-ID beim Server abfragen. */
  standPruefen: boolean;
  onFertig: () => void;
  /** Zum Verkauf, der Vorgang bleibt offen (Auszahlung noch offen). */
  onZurueck: () => void;
}) {
  const { api, vorgang, geraeteSpeicher, fenster, onVerloren } = props;
  const e = vorgang.ergebnis;
  // Stufe 2 kennt nur bar; die Prüfung bleibt für spätere Erstattungsarten
  const bar = (vorgang.anfrage.erstattungsart as string) === 'bar';
  // Belegtext aus der Abschlussantwort (`druck.text`); fehlt er, über den Druckabruf
  const [text, setText] = useState<string | null>(e.text);
  const [lade, setLade] = useState<string | null>(
    vorgang.ladeAngefordert === true
      ? 'Kassenlade wurde für diese Retoure bereits geöffnet.'
      : null,
  );
  // Geld übergeben (lokal bestätigt) und am Server vermerkt sind getrennt (Codex R1 M3)
  const [ausgezahltHier, setAusgezahltHier] = useState(false);
  // Server meldet die Auszahlung schon als bestätigt (Codex R2 K2): nie erneut anbieten
  const [serverBestaetigt, setServerBestaetigt] = useState(e.auszahlungBestaetigt);
  const ausgezahlt = ausgezahltHier || vorgang.auszahlungBestaetigt === true || serverBestaetigt;
  const [gemeldetHier, setGemeldetHier] = useState(false);
  const gemeldet = gemeldetHier || vorgang.auszahlungGemeldet === true || serverBestaetigt;
  const [meldet, setMeldet] = useState(false);
  const [ausHinweis, setAusHinweis] = useState<string | null>(null);
  const [autoDruck, setAutoDruck] = useState(false);
  const ladeOeffnen = props.ladeOeffnen;

  useEffect(() => {
    if (e.text !== null) {
      return;
    }
    let aktiv = true;
    api
      .belegText(vorgang.kassenId, e.beleg.belegnummer)
      .then((t) => {
        if (aktiv) {
          setText(t);
        }
      })
      .catch(() => undefined);
    return () => {
      aktiv = false;
    };
  }, [api, vorgang.kassenId, e.beleg.belegnummer, e.text]);

  // Lade: nur bar, nur nach dem signierten Beleg, je Vorgang genau einmal (Merker vor dem Öffnen)
  const ladeGestartet = useRef(false);
  useEffect(() => {
    if (!bar || ladeGestartet.current || vorgang.ladeAngefordert === true) {
      return;
    }
    ladeGestartet.current = true;
    void beanspruche(
      geraeteSpeicher,
      vorgang.kassenId,
      vorgang.vorgangId,
      'ladeAngefordert',
      fenster,
    )
      .then(async (erster) => {
        if (!erster) {
          setLade('Kassenlade wurde für diese Retoure bereits geöffnet.');
          return;
        }
        if (ladeOeffnen === undefined) {
          setLade('Keine Kassenlade angebunden – Lade mit dem Schlüssel öffnen.');
          return;
        }
        const r = await ladeOeffnen(e.beleg.belegnummer);
        setLade(r.ok === true ? 'Kassenlade ist offen' : r.text);
      })
      .catch((f: unknown) => {
        setLade(`Kassenlade nicht geöffnet: ${f instanceof Error ? f.message : String(f)}`);
      });
  }, [bar, geraeteSpeicher, vorgang, fenster, ladeOeffnen, e.beleg.belegnummer]);

  // Automatischer Druck: je Vorgang genau einmal, atomar beansprucht (Codex R1 M4); danach nur
  // noch ausdrücklich („Drucken“/Nachdruck in der Belegansicht bzw. unter „Belege“)
  const druckGeprueft = useRef(false);
  useEffect(() => {
    if (druckGeprueft.current || props.bridgeDruck === undefined) {
      return;
    }
    druckGeprueft.current = true;
    void beanspruche(
      geraeteSpeicher,
      vorgang.kassenId,
      vorgang.vorgangId,
      'druckAngefordert',
      fenster,
    )
      .then((erster) => {
        setAutoDruck(erster);
      })
      .catch(() => undefined);
  }, [geraeteSpeicher, vorgang.kassenId, vorgang.vorgangId, fenster, props.bridgeDruck]);

  /**
   * Codex R3 M3: Nach dem ersten Druckversuch (auch bei Fehler oder unklarem Ergebnis) gilt der
   * Retourenbeleg als gedruckt – jeder weitere Druck geht als Kopie (`kopie=1`), die Taste heißt
   * „Nachdruck (Kopie)“. Nach Neuladen gilt das schon, wenn der Autodruck beansprucht war.
   */
  const bridgeDruckRoh = props.bridgeDruck;
  const nr = e.beleg.belegnummer;
  const kasseBeleg = e.beleg.kassenId || vorgang.kassenId;
  if (vorgang.druckAngefordert === true && !istGedruckt(localStorage, kasseBeleg, nr)) {
    merkeGedruckt(localStorage, kasseBeleg, nr);
  }
  // Codex R5: Merker auch im Vorgangsdatensatz (druckAngefordert), nicht nur in localStorage
  const [browserGedruckt, setBrowserGedruckt] = useState(
    () => vorgang.druckAngefordert === true || istGedruckt(localStorage, kasseBeleg, nr),
  );
  const [browserKopie, setBrowserKopie] = useState(browserGedruckt);
  const retoureDruck = useMemo<BonDruck | undefined>(
    () =>
      bridgeDruckRoh === undefined
        ? undefined
        : (belegnummer, opt) =>
            bridgeDruckRoh(belegnummer, opt).finally(() => {
              merkeGedruckt(localStorage, kasseBeleg, belegnummer);
            }),
    [bridgeDruckRoh, kasseBeleg],
  );

  /** Bestätigung am Server vermerken (idempotent, genau der Betrag des Servers). */
  const melde = useCallback(async (): Promise<void> => {
    setMeldet(true);
    try {
      await api.auszahlungBestaetigen(vorgang.kassenId, vorgang.vorgangId, e.auszahlungCent);
      setGemeldetHier(true);
      setAusHinweis(null);
      await aktualisiereRetoureVorgang(
        geraeteSpeicher,
        vorgang.kassenId,
        vorgang.vorgangId,
        { auszahlungGemeldet: true },
        fenster,
      ).catch(() => null);
    } catch {
      setAusHinweis('Bestätigung am Server nicht vermerkt – bitte erneut senden.');
    } finally {
      setMeldet(false);
    }
  }, [api, geraeteSpeicher, vorgang.kassenId, vorgang.vorgangId, e.auszahlungCent, fenster]);

  // Nach Neuladen: Auszahlungsstand über die Vorgangs-ID abfragen. Bis zur Antwort keine
  // Auszahlung anbieten (Codex R3 K2); bei Fehler „Stand erneut prüfen“.
  const [standLage, setStandLage] = useState<'pruefe' | 'ok' | 'fehler'>(
    props.standPruefen && !e.auszahlungBestaetigt && vorgang.auszahlungBestaetigt !== true
      ? 'pruefe'
      : 'ok',
  );
  const [standLauf, setStandLauf] = useState(0);
  useEffect(() => {
    if (standLage !== 'pruefe') {
      return;
    }
    let aktiv = true;
    api
      .stand(vorgang.kassenId, vorgang.vorgangId)
      .then((r) => {
        if (!aktiv) {
          return;
        }
        if (r.art !== 'abgeschlossen') {
          setStandLage('fehler');
          return;
        }
        if (r.auszahlungBestaetigt) {
          setServerBestaetigt(true);
          void aktualisiereRetoureVorgang(
            geraeteSpeicher,
            vorgang.kassenId,
            vorgang.vorgangId,
            { auszahlungBestaetigt: true, auszahlungGemeldet: true },
            fenster,
          ).catch(() => null);
        }
        setStandLage('ok');
      })
      .catch(() => {
        if (aktiv) {
          setStandLage('fehler');
        }
      });
    return () => {
      aktiv = false;
    };
  }, [standLage, standLauf, api, geraeteSpeicher, vorgang.kassenId, vorgang.vorgangId, fenster]);

  // Nach Neuladen: bestätigt, aber nicht vermerkt → einmal nachmelden
  const nachgemeldet = useRef(false);
  useEffect(() => {
    if (!bar || !ausgezahlt || gemeldet || nachgemeldet.current) {
      return;
    }
    nachgemeldet.current = true;
    void melde();
  }, [bar, ausgezahlt, gemeldet, melde]);

  const bestaetigtRef = useRef(false);
  async function auszahlungBestaetigen(): Promise<void> {
    if (bestaetigtRef.current || ausgezahlt || standLage !== 'ok') {
      return;
    }
    bestaetigtRef.current = true;
    // erst im Gerätespeicher atomar beanspruchen (nur im eigenen Fenster, höchstens einmal –
    // Codex R3 K1), dann am Server (idempotent, höchstens ein Ereignis)
    const erster = await beanspruche(
      geraeteSpeicher,
      vorgang.kassenId,
      vorgang.vorgangId,
      'auszahlungBestaetigt',
      fenster,
    ).catch(() => false);
    if (!erster) {
      bestaetigtRef.current = false;
      onVerloren();
      return;
    }
    nachgemeldet.current = true;
    setAusgezahltHier(true);
    await melde();
  }

  const offenGrund = !bar
    ? null
    : !ausgezahlt && standLage === 'pruefe'
      ? 'Auszahlungsstand wird beim Server geprüft – noch nichts auszahlen …'
      : !ausgezahlt && standLage === 'fehler'
        ? 'Auszahlungsstand nicht prüfbar – nichts auszahlen, bis der Stand geklärt ist.'
        : !ausgezahlt
          ? lade !== null && lade !== 'Kassenlade ist offen'
            ? `${lade} – Bargeld erst nach Übergabe bestätigen.`
            : 'Bargeld übergeben und mit „Auszahlung bestätigen“ quittieren.'
          : !gemeldet
            ? (ausHinweis ?? 'Bestätigung wird am Server protokolliert …')
            : null;
  const lagerText =
    e.lager.length === 0
      ? 'Lager wird gebucht'
      : e.lager.every((l) => l.ergebnis === 'zugang' || l.ergebnis === 'kein_lagerartikel')
        ? 'Lager gebucht'
        : `Lager gebucht, ${String(e.lager.filter((l) => l.ergebnis === 'klaerung').length)} Position(en) als Klärung (beschädigt bzw. Frischware)`;

  return (
    <div className="retoure rt-ergebnis">
      <div className="karte rt-ergebnis-kopf" role="status">
        <span className="rt-marke">RÜCKGABE</span>
        <h2>
          {bar
            ? ausgezahlt
              ? 'Ausgezahlt'
              : standLage === 'ok'
                ? 'Bitte bar auszahlen'
                : 'Auszahlungsstand ungeklärt – nichts auszahlen'
            : 'Erstattung'}{' '}
          <strong className="rt-auszahlung">{euro(e.auszahlungCent)}</strong>
        </h2>
        {/* Drei getrennte Zustände (Nutzer 04.11.2026, 5) */}
        <ol className="rt-abschluss-zustaende">
          <li className="rt-zustand rt-zustand-ok">
            <strong>Retoure festgeschrieben</strong>
            <span>
              Beleg {e.beleg.belegnummer} signiert · {lagerText}
            </span>
          </li>
          {bar && gemeldet && ausgezahlt && (
            <li className="rt-zustand rt-zustand-ok">
              <strong>Bargeld ausbezahlt</strong>
              <span>Auszahlung bestätigt und am Server protokolliert.</span>
            </li>
          )}
          {offenGrund !== null && (
            <li className="rt-zustand rt-zustand-offen" role="alert">
              <strong>Auszahlung noch offen</strong>
              <span>{offenGrund}</span>
              <span>Nicht erneut retournieren – diese Rückgabe ist bereits festgeschrieben.</span>
            </li>
          )}
        </ol>
        <ul className="rt-zeilen">
          <li>
            <span>Bezug</span>
            <strong>
              {e.original.belegnummer || vorgang.anfrage.original.belegnummer} (Kasse{' '}
              {e.original.kassenId || vorgang.anfrage.original.kassenId})
            </strong>
          </li>
          {bar && (
            <li>
              <span>Kassenlade</span>
              <strong>{lade ?? 'wird geöffnet …'}</strong>
            </li>
          )}
          <li>
            <span>Bon</span>
            <strong>
              {props.bridgeDruck === undefined
                ? 'Kein Bondrucker – Beleg am Bildschirm; Nachdruck (Kopie) unter „Belege“.'
                : 'Bei einem Druckfehler nur „Nachdruck (Kopie)“ – nie neu abschließen.'}
            </strong>
          </li>
        </ul>
        <div className="rt-aktionen-zeile">
          {bar && !ausgezahlt && standLage === 'fehler' && (
            <button
              type="button"
              className="taste gross"
              onClick={() => {
                setStandLage('pruefe');
                setStandLauf((n) => n + 1);
              }}
            >
              Stand erneut prüfen
            </button>
          )}
          {bar && !ausgezahlt && standLage === 'ok' && (
            <button
              type="button"
              className="taste gross"
              // Figma RT2-6a: Bestätigen mit Enter (Scanner-Enter fängt useScanSchutz ab)
              autoFocus
              onClick={() => {
                void auszahlungBestaetigen();
              }}
            >
              Auszahlung bestätigen
            </button>
          )}
          {bar && ausgezahlt && !gemeldet && (
            <button
              type="button"
              className="taste gross"
              disabled={meldet}
              onClick={() => {
                void melde();
              }}
            >
              {meldet ? 'Wird protokolliert …' : 'Auszahlung bestätigen (erneut senden)'}
            </button>
          )}
          {(!bar || (ausgezahlt && gemeldet)) && (
            <button type="button" className="taste gross" onClick={props.onFertig}>
              Fertig – zurück zum Verkauf
            </button>
          )}
          {bar && !(ausgezahlt && gemeldet) && (
            <button type="button" className="taste zweit gross" onClick={props.onZurueck}>
              Zurück zum Verkauf (Auszahlung bleibt offen)
            </button>
          )}
        </div>
      </div>
      {retoureDruck === undefined && (
        // Ohne Bondrucker: Browserdruck; ab dem zweiten Mal nur als gekennzeichnete Kopie (R4)
        <div className="zeile kein-druck rt-aktionen-zeile">
          <button
            type="button"
            className="taste zweit"
            onClick={() => {
              void (async () => {
                // erster Druck atomar beansprucht; scheitert das (Speicherfehler, schon gedruckt),
                // gibt es nur noch die Kopie
                const erster =
                  !browserGedruckt &&
                  (await beanspruche(
                    geraeteSpeicher,
                    vorgang.kassenId,
                    vorgang.vorgangId,
                    'druckAngefordert',
                    fenster,
                  ).catch(() => false));
                merkeGedruckt(localStorage, kasseBeleg, nr);
                setBrowserGedruckt(true);
                if (!erster) {
                  setBrowserKopie(true);
                }
                window.setTimeout(() => {
                  window.print();
                  // danach (auch für Strg+P) nur noch als Kopie
                  setBrowserKopie(true);
                }, 0);
              })();
            }}
          >
            {browserGedruckt ? 'Nachdruck (Kopie)' : 'Beleg drucken'}
          </button>
        </div>
      )}
      <Belegansicht
        beleg={e.beleg}
        text={
          browserKopie
            ? `*** KOPIE – kein neuer Beleg ***\n${text ?? `Beleg ${e.beleg.belegnummer}`}`
            : text
        }
        autoDruck={autoDruck}
        // Druckfehler: nur „Nachdruck (Kopie)“ (Nutzer 6); Browserdruck nur über die Taste oben
        browserDruck={false}
        {...(retoureDruck === undefined ? {} : { bridgeDruck: retoureDruck })}
      />
    </div>
  );
}
